<template>
    <div class="bg-gray-100 pb-20">
        <TopLocationHero
            static-image-key="house-of-hungarian-music___hero"
            title-key="house_of_hungarian_music.title"
        />

        <div class="relative mt-4 sm:mt-12 z-1">
            <a id="main-content" class="fixed top-0" tabindex="-1"></a>

            <div class="container hidden sm:block">
                <Breadcrumb :items="breadcrumbItems" />
            </div>

            <TopLocationBackgroundShape1 />
        </div>

        <!-- * -->
        <!-- * -->
        <!-- * -->
        <!-- INTRO -->
        <!-- * -->
        <div class="relative sm:mt-8 z-2">
            <div class="container">
                <Type1Block
                    title-key="house_of_hungarian_music.intro.title"
                    description-key="house_of_hungarian_music.intro.description"
                    :img="require('assets/images/illustrations/buildings/house-of-hungarian-music.svg')"
                    building-name-key="house_of_hungarian_music.intro.building_name"
                    building-area-key="house_of_hungarian_music.intro.building_area"
                    building-architect-key="house_of_hungarian_music.intro.building_architect"
                    building-contractor-key="house_of_hungarian_music.intro.building_contractor"
                />
            </div>
        </div>

        <!-- * -->
        <!-- * -->
        <!-- * -->
        <!-- GALLERY WITH DESCRIPTION -->
        <!-- * -->
        <Type2Block
            v-if="galleryImages1"
            title-key="house_of_hungarian_music.appearance.title"
            description-key="house_of_hungarian_music.appearance.description"
            :images="galleryImages1"
            class="relative mt-16 sm:mt-20 xl:mt-24 z-1"
        />

        <!-- * -->
        <!-- * -->
        <!-- * -->
        <!-- GROUND FLOOR -->
        <!-- * -->
        <div class="overflow-hidden relative">
            <div class="container relative pt-32 sm:pt-28 lg:pt-28 xl:pt-32 z-1">
                <div
                    class="section-index-label top-8 sm:top-4 md:top-8 lg:top-20 xl:top-32 left-0 sm:left-0 lg:-left-4 section-index-label--white"
                >
                    0
                </div>
                <Type3Block
                    title-key="house_of_hungarian_music.floor_0.title"
                    description-key="house_of_hungarian_music.floor_0.description"
                    static-image-key="house-of-hungarian-music___floor-0"
                    class="relative"
                />
            </div>

            <div class="relative">
                <TopLocationBackgroundShape2
                    class="top-0 sm:-top-72 md:-top-80 lg:top-[-30rem] 2xl:top-[-35rem] -translate-y-1/2 sm:translate-y-0"
                />

                <div class="container relative z-2">
                    <!-- * -->
                    <!-- * -->
                    <!-- * -->
                    <!-- MOSAIQUE GALLERY -->
                    <!-- * -->
                    <MosaiqueGalleryBlock v-if="galleryImages2" :items="galleryImages2" class="mt-16 sm:mt-20" />

                    <!-- * -->
                    <!-- * -->
                    <!-- * -->
                    <!-- VERTICAL SLIDES -->
                    <!-- * -->
                    <Type5Block v-if="galleryImages3" :items="galleryImages3" class="mt-16 sm:mt-20" />
                </div>
            </div>
        </div>

        <!-- * -->
        <!-- * -->
        <!-- * -->
        <!-- -1ST FLOOR -->
        <!-- * -->
        <Type11Block
            :slides="[
                {
                    titleKey: 'house_of_hungarian_music.underground_floor_1.title',
                    descriptionKey: 'house_of_hungarian_music.underground_floor_1.description',
                    imageKey: 'house-of-hungarian-music___underground-slide-1',
                },
                {
                    titleKey: 'house_of_hungarian_music.underground_floor_2.title',
                    descriptionKey: 'house_of_hungarian_music.underground_floor_2.description',
                    imageKey: 'house-of-hungarian-music___underground-slide-2',
                },
                {
                    titleKey: 'house_of_hungarian_music.underground_floor_3.title',
                    descriptionKey: 'house_of_hungarian_music.underground_floor_3.description',
                    imageKey: 'house-of-hungarian-music___underground-slide-3',
                },
                {
                    titleKey: 'house_of_hungarian_music.underground_floor_4.title',
                    descriptionKey: 'house_of_hungarian_music.underground_floor_4.description',
                    imageKey: 'house-of-hungarian-music___underground-slide-4',
                },
                {
                    titleKey: 'house_of_hungarian_music.underground_floor_5.title',
                    descriptionKey: 'house_of_hungarian_music.underground_floor_5.description',
                    imageKey: 'house-of-hungarian-music___underground-slide-5',
                },
            ]"
        >
            <template #index>
                <div
                    class="order-1 section-index-label section-index-label--black top-[110vw] sm:top-12 xl:top-24 right-0 sm:right-unset sm:-left-36 opacity-30 sm:opacity-60"
                    style="letter-spacing: -40px"
                >
                    -1
                </div>
            </template>
        </Type11Block>

        <!-- * -->
        <!-- * -->
        <!-- * -->
        <!-- 1ST FLOOR -->
        <!-- * -->
        <div class="relative z-2">
            <TopLocationBackgroundShape4 class="top-36 sm:top-12 xl:top-12 3xl:top-0" />

            <div class="overflow-hidden">
                <div class="container grid grid-cols-12 base-responsive-gap pt-32 relative">
                    <div
                        class="section-index-label section-index-label--white top-8 sm:top-0 -right-12 sm:right-unset sm:-left-4 z-0"
                    >
                        1
                    </div>

                    <div
                        class="separator-border-top-line col-span-12 sm:col-span-4 lg:col-span-4 lg:col-start-2 z-1"
                    ></div>

                    <div class="col-span-12 sm:col-span-4 sm:col-start-1 lg:col-span-3 lg:col-start-2 relative">
                        <h2 class="header-1 mb-4 sm:mb-12 lg:mb-24">
                            <T t="house_of_hungarian_music.floor_1.title" />
                        </h2>

                        <h2 class="font-Poppins font-semibold text-3xl">
                            <T t="house_of_hungarian_music.floor_1.subtitle" />
                        </h2>
                        <p class="whitespace-pre-line"><T t="house_of_hungarian_music.floor_1.description" /></p>
                    </div>
                    <div class="col-span-12 sm:col-span-8 lg:col-span-7 lg:pt-8">
                        <StaticImage
                            img-key="house-of-hungarian-music___floor-1"
                            wrapper-class="aspect-w-1 aspect-h-1 sm:aspect-w-3 sm:aspect-h-2 relative base-responsive-rounding"
                            image-class="w-full h-full object-cover"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- * -->
        <!-- * -->
        <!-- * -->
        <!-- 2ND FLOOR -->
        <!-- * -->
        <div class="container relative z-3">
            <div class="grid grid-cols-12 base-responsive-gap pt-32 sm:pt-36 relative">
                <div
                    class="section-index-label section-index-label--white top-8 sm:top-6 lg:top-24 right-0 sm:right-unset sm:-left-4"
                >
                    2
                </div>

                <div
                    class="col-span-12 sm:col-span-4 sm:col-start-1 lg:col-span-4 lg:col-start-2 relative flex flex-col justify-between 3xl:justify-start separator-border-top-line"
                >
                    <h2 class="header-1 mb-4">
                        <T t="house_of_hungarian_music.floor_2.title" />
                    </h2>

                    <p class="lead whitespace-pre-line lg:mb-8 3xl:mt-12">
                        <T t="house_of_hungarian_music.floor_2.description" />
                    </p>
                </div>
                <div class="col-span-12 sm:col-span-8 lg:col-span-6 lg:mt-4">
                    <StaticImage
                        img-key="house-of-hungarian-music___floor-2-main"
                        wrapper-class="aspect-w-3 aspect-h-2 relative base-responsive-rounding"
                        image-class="w-full h-full object-cover"
                    />
                </div>
            </div>

            <!-- * -->
            <!-- * -->
            <!-- * -->
            <!-- MUSIC TEACHING SPACES & MEDIA LIBRARY -->
            <!-- * -->
            <div class="grid grid-cols-12 base-responsive-gap mt-12 sm:mt-16 lg:mt-20">
                <div class="order-2 sm:order-1 col-span-12 sm:col-span-6 lg:col-start-2">
                    <StaticImage
                        img-key="house-of-hungarian-music___floor-2-music"
                        wrapper-class="aspect-w-3 aspect-h-2 relative base-responsive-rounding"
                        image-class="w-full h-full object-cover"
                    />
                </div>
                <div class="order-1 sm:order-2 col-span-12 sm:col-span-5 lg:col-span-4 flex flex-col items-start mt-12">
                    <h2 class="header-3 mb-4"><T t="house_of_hungarian_music.floor_2.music.title" /></h2>
                    <p class="whitespace-pre-line">
                        <T t="house_of_hungarian_music.floor_2.music.description" />
                    </p>
                </div>
            </div>

            <Type7Block
                title-key="house_of_hungarian_music.floor_2.multimedia.title"
                description-key="house_of_hungarian_music.floor_2.multimedia.description"
                :images="galleryImages4"
                class="mt-12 sm:mt-16 lg:mt-20"
            />

            <TopLocationProjectPageBanner
                v-if="$t('house_of_hungarian_music.event_recommendation_button_url').length"
                :button-text="$t('house_of_hungarian_music.event_recommendation_button_label')"
                :button-url="$t('house_of_hungarian_music.event_recommendation_button_url')"
                label-text-key="house_of_hungarian_music.event_recommendation_text"
                type="event"
                class="mt-16 sm:mt-20"
            />

            <TopLocationProjectPageBanner
                :button-text="$t('house_of_hungarian_music.visit_project_button_label')"
                :button-url="$t('house_of_hungarian_music.visit_project_button_url')"
                label-text-key="house_of_hungarian_music.visit_project_text"
                class="mt-4 sm:mt-8"
            />

            <AwardsBlock
                v-if="awards"
                :title="awards.title"
                :description="awards.description"
                :media="awards.media"
                class="mt-16 sm:mt-20"
            />
        </div>
    </div>
</template>

<script>
import Type11Block from '@/components/Blocks/Type11Block/Type11Block.vue';
import TopLocationProjectPageBanner from '@/components/Pages/TopLocation/TopLocationProjectPageBanner.vue';
import AwardsBlock from '@/components/Blocks/AwardsBlock/AwardsBlock.vue';
import TopLocationHero from '@/components/Pages/TopLocation/Hero/Hero.vue';
import Type1Block from '@/components/Blocks/Type1Block/Type1Block.vue';
import Type2Block from '@/components/Blocks/Type2Block/Type2Block.vue';
import Type3Block from '@/components/Blocks/Type3Block/Type3Block.vue';
import Type5Block from '@/components/Blocks/Type5Block/Type5Block.vue';
import Type7Block from '@/components/Blocks/Type7Block/Type7Block.vue';
import MosaiqueGalleryBlock from '@/components/Blocks/MosaiqueGalleryBlock/MosaiqueGalleryBlock.vue';
import TopLocationBackgroundShape1 from '@/components/Pages/TopLocation/TopLocationBackgroundShape1.vue';
import TopLocationBackgroundShape2 from '@/components/Pages/TopLocation/TopLocationBackgroundShape2.vue';
import TopLocationBackgroundShape4 from '@/components/Pages/TopLocation/TopLocationBackgroundShape4.vue';

export default {
    name: 'HouseOfHungarianMusic',
    components: {
        Type11Block,
        AwardsBlock,
        TopLocationProjectPageBanner,
        TopLocationBackgroundShape4,
        TopLocationBackgroundShape1,
        TopLocationBackgroundShape2,
        TopLocationHero,
        Type1Block,
        Type2Block,
        Type3Block,
        Type5Block,
        Type7Block,
        MosaiqueGalleryBlock,
    },
    async asyncData({ $axios, i18n }) {
        let galleryImages1 = null;
        let galleryImages2 = null;
        let galleryImages3 = null;
        let galleryImages4 = null;
        let awards = null;

        await Promise.allSettled([
            $axios.$get('/galleries/reference/top-location___house-of-hungarian-music-1'),
            $axios.$get('/galleries/reference/top-location___house-of-hungarian-music-2'),
            $axios.$get('/galleries/reference/top-location___house-of-hungarian-music-3'),
            $axios.$get('/galleries/reference/top-location___house-of-hungarian-music-4'),
            $axios.$get('/awards/reference/house-of-hungarian-music'),
        ])
            .then(([galleryImages1Res, galleryImages2Res, galleryImages3Res, galleryImages4Res, awardsRes]) => {
                if (galleryImages1Res.status === 'fulfilled') {
                    galleryImages1 = galleryImages1Res.value.slides;
                }

                if (galleryImages2Res.status === 'fulfilled') {
                    galleryImages2 = galleryImages2Res.value.slides;
                }

                if (galleryImages3Res.status === 'fulfilled') {
                    galleryImages3 = galleryImages3Res.value.slides;
                }

                if (galleryImages4Res.status === 'fulfilled') {
                    galleryImages4 = galleryImages4Res.value.slides;
                }

                if (awardsRes.status === 'fulfilled' && awardsRes.value.is_on_top_locations) {
                    awards = awardsRes.value;
                }
            })
            .catch((err) => {
                console.log(err);
            });

        const breadcrumbItems = [];

        breadcrumbItems.push([i18n.t('navigation.top_locations'), 'top-locations', true]);
        breadcrumbItems.push([i18n.t('house_of_hungarian_music.title'), 'house-of-hungarian-music']);

        return {
            galleryImages1,
            galleryImages2,
            galleryImages3,
            galleryImages4,
            awards,
            breadcrumbItems,
        };
    },
    head() {
        return {
            title: this.$getPageTitle('top-locations.house-of-hungarian-music'),
            meta: this.$getPageMetaData('top-locations.house-of-hungarian-music'),
        };
    },
};
</script>

<style>
.section-index-label--black {
    background: linear-gradient(115deg, rgba(0, 0, 0, 0.75) 19.21%, rgba(0, 0, 0, 0) 80%);
}

.section-index-label--white {
    background: linear-gradient(180deg, #fff 0%, rgba(255, 255, 255, 0.91) 18.3%, rgba(255, 255, 255, 0) 100%);
}

.section-index-label {
    font-family: 'DIN Pro', sans-serif;
    @apply font-bold absolute text-[20rem] sm:text-[25rem] leading-[15rem] sm:leading-[20rem];
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
}
</style>
